/* ============================================================
   S10 PDP designer audio intro — 2026-07-29

   Custom element for the product-page audio player.
   - preload="none": the mp3 is not fetched until the visitor presses play,
     so the clip never costs page weight or LCP.
   - Never autoplays.
   - Only one clip plays at a time across the page.
   - Pushes engagement events to dataLayer (GTM -> GA4). Event/param setup
     is documented in SHOPIFY_PDP_AUDIO_HANDOFF_2026-07-29.md.
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('s10-audio-intro')) return;

  const MILESTONES = [25, 50, 75];

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const pushEvent = (params) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 's10_pdp_audio' }, params));
  };

  class S10AudioIntro extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.wrapper = this.querySelector('.s10-audio');
      this.audio = this.querySelector('audio');
      this.toggle = this.querySelector('.s10-audio__toggle');
      this.seek = this.querySelector('.s10-audio__seek');
      this.timeEl = this.querySelector('.s10-audio__time');
      this.durationEl = this.querySelector('.s10-audio__duration');

      if (!this.audio || !this.toggle) return;

      this.milestonesFired = {};
      this.hasPlayed = false;
      this.scrubbing = false;
      this.playLabel = this.toggle.dataset.labelPlay || 'Play';
      this.pauseLabel = this.toggle.dataset.labelPause || 'Pause';

      // The default portrait is a theme asset, and asset_url happily builds a
      // URL for a file nobody uploaded. Drop the image instead of leaving a
      // broken-image icon sitting behind the play mark. The completeness check
      // covers the case where the request already failed before this ran.
      const photo = this.querySelector('.s10-audio__portrait-img');
      if (photo) {
        const dropPhoto = () => photo.remove();
        photo.addEventListener('error', dropPhoto);
        if (photo.complete && photo.naturalWidth === 0) dropPhoto();
      }

      this.toggle.addEventListener('click', () => this.onToggleClick());
      this.audio.addEventListener('loadedmetadata', () => this.onLoadedMetadata());
      this.audio.addEventListener('timeupdate', () => this.onTimeUpdate());
      this.audio.addEventListener('play', () => this.onPlay());
      this.audio.addEventListener('pause', () => this.setPausedState());
      this.audio.addEventListener('ended', () => this.onEnded());

      if (this.seek) {
        this.seek.addEventListener('input', () => this.onSeekInput());
        ['mousedown', 'touchstart', 'keydown'].forEach((evt) =>
          this.seek.addEventListener(evt, () => {
            this.scrubbing = true;
          })
        );
        ['mouseup', 'touchend', 'keyup'].forEach((evt) =>
          this.seek.addEventListener(evt, () => {
            this.scrubbing = false;
          })
        );
      }
    }

    disconnectedCallback() {
      if (this.audio && !this.audio.paused) this.audio.pause();
    }

    /* ---- tracking ---- */

    trackingParams(extra) {
      return Object.assign(
        {
          audio_product_id: this.dataset.productId || '',
          audio_product: this.dataset.productTitle || '',
          audio_clip: this.dataset.clipName || ''
        },
        extra
      );
    }

    /* ---- playback ---- */

    onToggleClick() {
      if (!this.audio.paused) {
        this.audio.pause();
        return;
      }
      this.pauseOthers();
      const attempt = this.audio.play();
      // Blocked or unsupported source: leave the UI in its paused state.
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }

    pauseOthers() {
      document.querySelectorAll('s10-audio-intro').forEach((player) => {
        if (player !== this && player.audio && !player.audio.paused) player.audio.pause();
      });
    }

    onPlay() {
      if (this.wrapper) this.wrapper.classList.add('is-playing');
      this.toggle.setAttribute('aria-label', this.pauseLabel);
      this.toggle.setAttribute('aria-pressed', 'true');

      if (this.hasPlayed) return;
      this.hasPlayed = true;
      pushEvent(
        this.trackingParams({
          audio_event: 'play',
          audio_duration: Math.round(this.audio.duration || 0)
        })
      );
    }

    setPausedState() {
      if (this.wrapper) this.wrapper.classList.remove('is-playing');
      this.toggle.setAttribute('aria-label', this.playLabel);
      this.toggle.setAttribute('aria-pressed', 'false');
    }

    onEnded() {
      this.setPausedState();
      if (this.seek) this.seek.value = 0;
      this.renderTime(0);

      if (this.milestonesFired.complete) return;
      this.milestonesFired.complete = true;
      pushEvent(this.trackingParams({ audio_event: 'complete', audio_percent: 100 }));
    }

    onLoadedMetadata() {
      if (this.seek) this.seek.removeAttribute('disabled');
      // Duration first: renderTime checks whether the label is already showing
      // the total, so reversing these paints "0:00 / 1:19" until the first
      // timeupdate corrects it.
      this.renderDuration();
      this.renderTime(0);
    }

    // The label carries the clip's real length, read off the file. It stays
    // hidden if the metadata never arrives, so a failed request degrades to a
    // label with no duration rather than to "--:--".
    renderDuration() {
      if (!this.durationEl) return;
      const duration = this.audio.duration;
      if (!isFinite(duration) || duration <= 0) return;
      this.durationEl.textContent = formatTime(duration);
      this.durationEl.hidden = false;
    }

    onTimeUpdate() {
      if (this.scrubbing) return;
      const duration = this.audio.duration;
      if (!isFinite(duration) || duration <= 0) return;

      const percent = (this.audio.currentTime / duration) * 100;
      if (this.seek) this.seek.value = percent;
      this.renderTime(this.audio.currentTime);

      MILESTONES.forEach((mark) => {
        if (percent < mark || this.milestonesFired[mark]) return;
        this.milestonesFired[mark] = true;
        pushEvent(this.trackingParams({ audio_event: 'progress', audio_percent: mark }));
      });
    }

    renderTime(current) {
      if (!this.timeEl) return;
      // When the label already carries the total, repeating it beside the
      // scrubber is noise — show elapsed only.
      if (this.durationEl && !this.durationEl.hidden) {
        this.timeEl.textContent = formatTime(current);
        return;
      }
      const duration = this.audio.duration;
      const durationText = isFinite(duration) && duration > 0 ? formatTime(duration) : '--:--';
      this.timeEl.textContent = `${formatTime(current)} / ${durationText}`;
    }

    onSeekInput() {
      const duration = this.audio.duration;
      if (!isFinite(duration) || duration <= 0) return;
      const target = (parseFloat(this.seek.value) / 100) * duration;
      this.audio.currentTime = target;
      this.renderTime(target);
    }
  }

  customElements.define('s10-audio-intro', S10AudioIntro);
})();
